import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActiveChild } from '../context/ActiveChildContext';
import { AppLayout, GlassCard } from './Layout';
import { Button } from './ui/button';
import { Brain, Calculator, Mail, Eye, Users, Send, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = {
  quiz: {
    icon: Brain,
    accent: 'purple',
    title: 'Personality Quiz',
    childTask: 'Know themselves',
    subtitle: 'This is part of your child\'s self-discovery journey.',
    description:
      'The Personality Quiz helps your child uncover their strengths, interests, and natural fit for different career paths. The answers are personal, only your child can take this for themselves.',
    bullets: [
      'Discovers strengths and natural inclinations',
      'Maps their personality type to careers that fit',
      'Surfaces interests they might not have considered',
    ],
  },
  calculator: {
    icon: Calculator,
    accent: 'emerald',
    title: 'Lifestyle Calculator',
    childTask: 'Design their life',
    subtitle: 'This is your child\'s "design the future they want" exercise.',
    description:
      'The Lifestyle Calculator lets your child describe the life they want, where they live, the home, hobbies, family, and works backward to the salary needed to fund it. It\'s most powerful when they own the answers.',
    bullets: [
      'Lets them picture the life they want at 28',
      'Reverse-engineers the salary needed',
      'Connects lifestyle goals to career choices',
    ],
  },
};

/**
 * For parent users, replaces the actual Quiz / Lifestyle Calculator page with
 * an educational explainer + actions:
 *   - "See a sample", runs the underlying page so the parent understands what
 *     their child is doing (results aren't saved to the child).
 *   - "Email {child} a reminder", sends a nudge to the active child.
 *
 * For non-parents, renders the underlying page unchanged.
 */
export default function ParentSelfReflectionGate({ kind, children: pageContent }) {
  const { user, getAuthHeader } = useAuth();
  const { activeChild, children: kidList } = useActiveChild();
  const [showSample, setShowSample] = useState(false);
  const [sending, setSending] = useState(false);

  const isParent = user?.role === 'parent';
  if (!isParent) return pageContent;

  const cfg = PRESETS[kind];
  if (!cfg) return pageContent;

  const Icon = cfg.icon;
  const childName = activeChild?.full_name || activeChild?.name || activeChild?.first_name;
  const firstName = childName ? childName.split(' ')[0] : null;

  if (showSample) {
    // Persist sample-mode flag so child pages and API calls can detect it.
    // Cleared when the user exits sample mode below.
    try { sessionStorage.setItem('fp_parent_sample_mode', '1'); } catch {}
    // Wrap the underlying page with a thin "exit sample mode" bar so the parent
    // can always get back to the educational explainer.
    return (
      <>
        <div
          data-testid="parent-sample-exit-bar"
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-3 border-b border-blue-500/20 bg-[linear-gradient(90deg,rgba(30,58,138,0.72)_0%,rgba(67,56,202,0.62)_50%,rgba(30,58,138,0.72)_100%)] backdrop-blur-xl px-4 py-2.5 shadow-[0_8px_32px_-12px_rgba(59,130,246,0.35)]"
        >
          <p className="text-xs sm:text-sm text-blue-50">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-white/10 ring-1 ring-white/15 px-2 py-0.5 font-semibold text-white">
              <Eye className="w-3 h-3" /> Sample mode
            </span>
            <span className="mx-2 text-blue-200/60">·</span>
            <span className="text-blue-100">Your answers won't save to {firstName || 'your child'}'s profile.</span>
          </p>
          <button
            type="button"
            onClick={() => {
              setShowSample(false);
              try { sessionStorage.removeItem('fp_parent_sample_mode'); } catch {}
            }}
            data-testid="parent-sample-exit"
            className="shrink-0 rounded-md bg-white/10 hover:bg-white/20 ring-1 ring-white/15 px-3 py-1 text-xs font-medium text-white transition-colors"
          >
            ← Back to overview
          </button>
        </div>
        <div className="pt-12">{pageContent}</div>
      </>
    );
  }
  const accentMap = {
    purple: { bg: 'from-purple-600/15 to-pink-600/15', border: 'border-purple-500/30', icon: 'text-purple-300', iconBg: 'bg-purple-500/20' },
    emerald: { bg: 'from-emerald-600/15 to-teal-600/15', border: 'border-emerald-500/30', icon: 'text-emerald-300', iconBg: 'bg-emerald-500/20' },
  };
  const a = accentMap[cfg.accent] || accentMap.purple;

  const sendReminder = async () => {
    if (!activeChild) return;
    setSending(true);
    try {
      await axios.post(`${API}/family/remind-child`, {
        child_id: activeChild.id || activeChild.user_id,
        section: cfg.title,
      }, { ...getAuthHeader(), withCredentials: true });
      toast.success(`Reminder sent to ${firstName}`);
    } catch (e) {
      // Endpoint may not exist yet, fall back to a friendly message
      toast.success(`We'll let ${firstName} know to check the ${cfg.title}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/70 via-slate-800/50 to-slate-900/70 backdrop-blur-xl p-6 sm:p-8 shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_16px_48px_-20px_rgba(0,0,0,0.6)]">
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-12 h-12 rounded-xl ${a.iconBg} flex items-center justify-center ring-1 ring-white/10`}>
              <Icon className={`w-6 h-6 ${a.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-blue-300/80 mb-1">
                Your child's journey · {cfg.childTask}
              </p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-2">
                {cfg.title}
              </h1>
              <p className="text-slate-300 text-sm sm:text-base">{cfg.subtitle}</p>
            </div>
          </div>
        </div>

        <GlassCard className="mb-6">
          <p className="text-slate-300 leading-relaxed mb-5">{cfg.description}</p>
          <ul className="space-y-2 mb-2">
            {cfg.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                <ArrowRight className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </GlassCard>

        {/* Actions */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Sample / preview */}
          <GlassCard className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-300" />
              <p className="text-sm font-medium text-white">Try a sample</p>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed mb-4 flex-1">
              Walk through the {cfg.title} as a preview so you understand what {firstName || 'your child'} sees and how to help. Your answers aren't saved to their profile.
            </p>
            <Button
              onClick={() => setShowSample(true)}
              data-testid="parent-gate-try-sample"
              className="w-full justify-center bg-blue-600 hover:bg-blue-500 text-white"
            >
              Try it Yourself!
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </GlassCard>

          {/* Reminder */}
          <GlassCard className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-amber-300" />
              <p className="text-sm font-medium text-white">
                Nudge {firstName || 'your child'}
              </p>
            </div>
            <p className="text-xs text-slate-300 leading-relaxed mb-4 flex-1">
              {kidList.length === 0
                ? 'Add a child to your family before you can send a reminder.'
                : `Send ${firstName || 'your child'} a friendly email reminder to complete the ${cfg.title}.`}
            </p>
            <Button
              onClick={sendReminder}
              disabled={!activeChild || sending}
              data-testid="parent-gate-send-reminder"
              className="w-full justify-center"
            >
              {sending ? 'Sending…' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send reminder
                </>
              )}
            </Button>
          </GlassCard>
        </div>

        {/* Helper note */}
        <div className="mt-6 flex items-start gap-2 text-xs text-slate-300">
          <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <p>
            You can switch which child this reminder is for using the active-child pill in the sidebar.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
